import React, { useCallback, useEffect, useRef, useState } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { SectionTag } from '../ui/SectionTag';
import { lookInsideImages } from '../../data/content';
export function LookInside() {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const total = lookInsideImages.length;
  const goTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, total - 1));
    itemRefs.current[clamped]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest'
    });
  }, [total]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        let closest = 0;
        let closestDistance = Infinity;
        itemRefs.current.forEach((item, i) => {
          if (!item) return;
          const distance = Math.abs(item.offsetLeft - container.scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = i;
          }
        });
        setActiveIndex(closest);
        ticking = false;
      });
    };
    container.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex(i => i === null ? i : Math.min(i + 1, total - 1));
      if (e.key === 'ArrowLeft') setLightboxIndex(i => i === null ? i : Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, total]);
  const activeImage = lightboxIndex !== null ? lookInsideImages[lightboxIndex] : null;
  return <section className="bg-white py-10 sm:py-14" data-id="element-99" data-mp="3k9ccilt50zsy">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6" data-id="element-100" data-mp="3k8jf4bg2jmnh">
        <SectionTag icon="👀" data-id="element-101" data-mp="236aabf99p46">Vistazo al interior</SectionTag>
        <h2 className="mt-5 font-heading text-3xl font-extrabold uppercase leading-tight text-navy sm:text-4xl" data-id="element-102" data-mp="21myax0mu087">
          Así son las clases y fichas que puedes llevar al aula
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted sm:text-lg" data-id="element-103" data-mp="bspzdytkaf7h">
          Mira una guía docente y cinco fichas reales de Primaria, ESO y Bachillerato. Desliza y toca
          cualquier página para ampliarla.
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-6xl px-4 sm:px-6" data-id="element-104" data-mp="fvmhlof6w9aw">
        <div ref={containerRef} className="scrollbar-hide flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2" data-id="element-105" data-mp="ft1hp4ygc3fs">
          {lookInsideImages.map((image, i) => <m.div key={image.src} ref={el => {
          itemRefs.current[i] = el;
        }} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i % 3 * 0.08
        }} className="w-[78%] flex-shrink-0 snap-start sm:w-[46%] lg:w-[31%]" data-id="element-106" data-mp="14eob6p8wf2ck" data-mpi={i}>
              <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Ampliar página: ${image.title}`} className="block w-full overflow-hidden rounded-xl border border-navy/5 bg-white shadow-card transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange" data-id="element-107" data-mp="liy6gu9kzruz">
                <img src={image.src} alt={image.alt} loading="lazy" decoding="async" className="aspect-[1/1.414] w-full object-cover" data-id="element-108" data-mp="3qvbz8z5ehndh" />
              </button>
              <p className="mt-3 font-heading text-sm font-semibold text-navy" data-id="element-109" data-mp="3h3pjqqm1524x">{image.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted" data-id="element-110" data-mp="3d3osu6hquygz">{image.caption}</p>
            </m.div>)}
        </div>

        <div className="mt-6 flex items-center justify-center gap-4" data-id="element-111" data-mp="335r7bqr9rfs2">
          <button type="button" onClick={() => goTo(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Página anterior" className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors hover:bg-navy/5 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-112" data-mp="334g35ptww60f">
            <ChevronLeftIcon size={18} data-id="element-113" data-mp="sf10cu3ibllb" />
          </button>
          <span className="font-heading text-sm font-semibold text-navy" aria-live="polite" data-id="element-114" data-mp="2abl0ymh27z25">
            {activeIndex + 1} / {total}
          </span>
          <button type="button" onClick={() => goTo(activeIndex + 1)} disabled={activeIndex === total - 1} aria-label="Página siguiente" className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors hover:bg-navy/5 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-115" data-mp="2f8usc7o0eksm">
            <ChevronRightIcon size={18} data-id="element-116" data-mp="1wc3ggjasr7yl" />
          </button>
        </div>

        <p className="mx-auto mt-6 max-w-2xl text-center text-sm leading-relaxed text-ink" data-id="element-117" data-mp="20cvoj97xn6ta">
          Cada clase combina una guía para el profesor, una ficha para el alumnado y soluciones identificadas
          con el mismo código. Las fichas también se entregan separadas para imprimir.
        </p>
      </div>

      <AnimatePresence data-id="element-118" data-mp="3gqdeylv3ulqo">
        {activeImage && <m.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-navy/90 p-4" role="dialog" aria-modal="true" aria-label={activeImage.title} onClick={() => setLightboxIndex(null)} data-id="element-119" data-mp="1gp5jxdsb3puq">
            <m.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} className="relative max-h-[90vh] w-full max-w-md" onClick={e => e.stopPropagation()} data-id="element-120" data-mp="xy4noxikm8ev">
              <button type="button" onClick={() => setLightboxIndex(null)} aria-label="Cerrar" className="absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange" data-id="element-121" data-mp="12vxqdgko0kae">
                <XIcon size={18} data-id="element-122" data-mp="2qozwstazm65k" />
              </button>
              <img src={activeImage.src} alt={activeImage.alt} className="max-h-[80vh] w-full rounded-xl object-contain" data-id="element-123" data-mp="2cws5vi6dx9es" />
              <p className="mt-3 text-center font-heading text-sm font-semibold text-white" data-id="element-124" data-mp="34mojm6om7nd9">{activeImage.title}</p>
            </m.div>
          </m.div>}
      </AnimatePresence>
    </section>;
}